// ============================================
// FILE: frontend/src/pages/UserConnectionsPage.jsx
// PURPOSE: Display user's connections with chat access
// FIXED: Removed back button, made stats + help collapsible
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useConnections } from '../hooks/useConnections';
import { useMessages } from '../hooks/useMessages';
import { useNotification } from '../hooks/useNotification';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { ConnectionsList } from '../components/messaging/ConnectionsList';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { 
  Loader2, 
  AlertCircle, 
  MessageCircle, 
  Users, 
  TrendingUp,
  RefreshCw,
  ChevronDown,
  X
} from 'lucide-react';

export default function UserConnectionsPage() {
  console.log('🎨 [UserConnectionsPage] Page mounted');

  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError } = useNotification();

  const {
    userConnections,
    loading,
    error,
    fetchUserConnections
  } = useConnections();

  const {
    getTotalUnreadMessages
  } = useMessages();

  const [totalUnread, setTotalUnread] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statsExpanded, setStatsExpanded] = useState(false); // NEW: Collapsible stats
  const [helpExpanded, setHelpExpanded] = useState(false); // NEW: Collapsible help
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inProgress: 0,
    completed: 0
  });

  // ========================================
  // INITIAL LOAD
  // ========================================

  useEffect(() => {
    console.log('[UserConnectionsPage] 🔐 Checking authentication...');

    if (!user) {
      console.error('[UserConnectionsPage] ❌ User not authenticated');
      navigate('/login?redirect=/my-connections');
      return;
    }

    console.log('[UserConnectionsPage] ✅ User authenticated:', user.email);
    loadConnections();
  }, [user, navigate]);

  // ========================================
  // LOAD CONNECTIONS
  // ========================================

  const loadConnections = useCallback(async () => {
    console.log('[UserConnectionsPage] 📥 Loading connections...');

    try {
      const response = await fetchUserConnections();
      console.log('[UserConnectionsPage] ✅ Connections loaded:', {
        count: response.connections?.length
      });

      // Calculate stats
      if (response.connections) {
        calculateStats(response.connections);
      }

      // Fetch unread count
      fetchUnreadCount();

    } catch (err) {
      console.error('[UserConnectionsPage] ❌ Failed to load connections:', err);
      showError('Error', 'Failed to load your connections');
    }
  }, [fetchUserConnections, showError]);

  // ========================================
  // CALCULATE STATS
  // ========================================

  const calculateStats = (connections) => {
    console.log('[UserConnectionsPage] 📊 Calculating stats...');

    const newStats = {
      total: connections.length,
      active: connections.filter(c => c.status === 'connected').length,
      inProgress: connections.filter(c => c.status === 'deal_in_progress').length,
      completed: connections.filter(c => c.status === 'deal_completed').length
    };

    console.log('[UserConnectionsPage] Stats calculated:', newStats);
    setStats(newStats);
  };

  // ========================================
  // FETCH UNREAD MESSAGE COUNT
  // ========================================

  const fetchUnreadCount = async () => {
    console.log('[UserConnectionsPage] 📬 Fetching unread message count...');

    try {
      const count = await getTotalUnreadMessages();
      console.log('[UserConnectionsPage] ✅ Unread count:', count);
      setTotalUnread(count || 0);
    } catch (err) {
      console.error('[UserConnectionsPage] ❌ Failed to fetch unread count:', err);
      // Don't show error to user, just log it
    }
  };

  // ========================================
  // REFRESH HANDLER
  // ========================================

  const handleRefresh = useCallback(async () => {
    console.log('[UserConnectionsPage] 🔄 Manual refresh triggered');
    setIsRefreshing(true);

    try {
      await loadConnections();
      console.log('[UserConnectionsPage] ✅ Refresh complete');
    } catch (err) {
      console.error('[UserConnectionsPage] ❌ Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [loadConnections]);

  // ========================================
  // LOADING STATE
  // ========================================

  if (loading && userConnections.length === 0) {
    console.log('[UserConnectionsPage] ⏳ Initial loading state');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center px-4">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary" />
            <p className="text-xs sm:text-sm text-muted-foreground">Loading your connections...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================

  if (error && userConnections.length === 0) {
    console.error('[UserConnectionsPage] ❌ Error state:', error);
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
          <Alert variant="destructive">
            <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
            <AlertTitle className="text-xs sm:text-sm">Error Loading Connections</AlertTitle>
            <AlertDescription className="text-xs sm:text-sm">{error}</AlertDescription>
          </Alert>
          <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:mt-4">
            <Button onClick={handleRefresh} disabled={isRefreshing} className="text-xs sm:text-sm h-9 sm:h-10">
              {isRefreshing ? (
                <>
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Retry
                </>
              )}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  console.log('[UserConnectionsPage] 🎨 Rendering page with connections');

  // ========================================
  // MAIN RENDER
  // ========================================

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow container py-4 sm:py-6 space-y-4 sm:space-y-6 px-4">
        {/* ========================================
            PAGE HEADER - REMOVED BACK BUTTON
            ======================================== */}
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-4"
          >
            {isRefreshing ? (
              <>
                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                <span className="hidden sm:inline">Refreshing...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </>
            )}
          </Button>
        </div>

        {/* ========================================
            TITLE & DESCRIPTION
            ======================================== */}
        <div>
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">My Connections</h1>
            {totalUnread > 0 && (
              <Badge variant="destructive" className="h-5 sm:h-6 px-1.5 sm:px-2 rounded-full text-[10px] sm:text-xs">
                {totalUnread} unread
              </Badge>
            )}
          </div>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
            View and chat with your matched partners
          </p>
        </div>

        {/* ========================================
            STATS CARDS - NOW COLLAPSIBLE
            ======================================== */}
        <Card>
          <CardHeader 
            className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
            onClick={() => setStatsExpanded(!statsExpanded)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base md:text-lg">Connection Statistics</CardTitle>
              {statsExpanded ? (
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>

          {statsExpanded && (
            <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
              <div className="flex flex-col gap-2 sm:gap-3">
                {/* Total Connections */}
                <div className="bg-gray-100 text-gray-800 p-3 sm:p-4 rounded-lg transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 sm:gap-3">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium truncate">Total Connections</span>
                  <span className="text-xs sm:text-sm font-bold ml-auto">{stats.total}</span>
                </div>

                {/* Active Connections */}
                <div className="bg-green-500 hover:bg-green-600 text-white p-3 sm:p-4 rounded-lg transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 sm:gap-3">
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium truncate">Active</span>
                  <span className="text-xs sm:text-sm font-bold ml-auto">{stats.active}</span>
                </div>

                {/* In Progress */}
                <div className="bg-blue-500 hover:bg-blue-600 text-white p-3 sm:p-4 rounded-lg transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 sm:gap-3">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium truncate">In Progress</span>
                  <span className="text-xs sm:text-sm font-bold ml-auto">{stats.inProgress}</span>
                </div>

                {/* Completed */}
                <div className="bg-emerald-500 hover:bg-emerald-600 text-white p-3 sm:p-4 rounded-lg transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 sm:gap-3">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium truncate">Completed</span>
                  <span className="text-xs sm:text-sm font-bold ml-auto">{stats.completed}</span>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* ========================================
            CONNECTIONS LIST
            ======================================== */}
        <ConnectionsList
          connections={userConnections}
          loading={loading}
          onRefresh={handleRefresh}
          autoRefresh={true}
          refreshInterval={45000}
        />

        {/* ========================================
            EMPTY STATE
            ======================================== */}
        {userConnections.length === 0 && !loading && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8 sm:py-10 md:py-12 gap-3 sm:gap-4 p-4 sm:p-6">
              <MessageCircle className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground" />
              <div className="text-center space-y-1.5 sm:space-y-2">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold">No Connections Yet</h3>
                <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
                  Once you're matched with an investor or landowner, your connections will appear here.
                  You'll be able to chat with them directly through our messaging system.
                </p>
              </div>
              <Button variant="outline" onClick={() => navigate('/jv/my-submission')} className="text-xs sm:text-sm h-9 sm:h-10">
                View My Submission
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ========================================
            HELP TEXT - NOW COLLAPSIBLE
            ======================================== */}
        {userConnections.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader 
              className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-blue-100/50 transition-colors"
              onClick={() => setHelpExpanded(!helpExpanded)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs sm:text-sm text-blue-900 flex items-center gap-2">
                  <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                  How to Use Connections
                </CardTitle>
                {helpExpanded ? (
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-blue-900" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-blue-900" />
                )}
              </div>
            </CardHeader>
            
            {helpExpanded && (
              <CardContent className="text-[10px] sm:text-xs text-blue-800 space-y-1.5 sm:space-y-2 p-3 sm:p-4 md:p-6 pt-0">
                <p>
                  • Click on any connection to open the chat window and start messaging
                </p>
                <p>
                  • Unread messages are indicated by a red badge with the count
                </p>
                <p>
                  • You can view connection details including match score and status
                </p>
                <p>
                  • Messages update in real-time - no need to refresh manually
                </p>
              </CardContent>
            )}
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
}